import { useCallback, useMemo } from 'react'

import SubscriptionEvents from 'Pods/Shared/Constants/subscriptionEvents'
import useTracker from 'Pods/Shared/Hooks/useTracker'

import { EMPTY_OBJECT } from 'Utils/stableDefaults'

const PAGE_TYPE = 'subscription_settings'
// Where the cancel flow was entered from; meaningless outside it, so it rides only on
// cancelFlowPayload.
const ENTRY_SURFACE = 'settings_plan_card'
const RESUME_SUCCESS_FLUSH_TIMEOUT_MS = 1000

const useSubscriptionSettingsTracking = (trackingData = EMPTY_OBJECT) => {
  const { trackEvent } = useTracker()
  const { tier = null, planType, daysSinceSubStart, downloadsUsed } = trackingData

  // tier stays null when unknown; the rest are omitted rather than sent empty.
  const basePayload = useMemo(() => ({
    page_type: PAGE_TYPE,
    subscription_tier: tier,
    ...(planType && { plan_type: planType }),
    ...(Number.isInteger(daysSinceSubStart) && { days_since_sub_start: daysSinceSubStart }),
    ...(Number.isInteger(downloadsUsed) && { downloads_used_this_period: downloadsUsed }),
  }), [tier, planType, daysSinceSubStart, downloadsUsed])

  const cancelFlowPayload = useMemo(
    () => ({ ...basePayload, entry_surface: ENTRY_SURFACE }),
    [basePayload]
  )

  const trackSectionView = useCallback(() => {
    trackEvent(SubscriptionEvents.SETTINGS_VIEWED, basePayload)
  }, [trackEvent, basePayload])

  const trackCancelClick = useCallback((subscriptionId, entryStep) => {
    trackEvent(SubscriptionEvents.CANCEL_CLICK, {
      ...cancelFlowPayload,
      subscription_id: subscriptionId,
      entry_step: entryStep,
    })
  }, [trackEvent, cancelFlowPayload])

  // Only the fact that the "other" box was filled reaches GA4, never its text (PII). The text
  // is persisted by /api/subscriptions/cancellation_reason, so no signal is lost.
  const trackCancellationConfirmed = useCallback((reason, subscriptionId, customReason) => {
    const payload = {
      ...cancelFlowPayload,
      reason,
      subscription_id: subscriptionId,
    }

    if (customReason) {
      payload.custom_reason_provided = true
    }

    trackEvent(SubscriptionEvents.CANCELLATION_CONFIRMED, payload)
  }, [trackEvent, cancelFlowPayload])

  const trackRenewClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RENEW_CLICK, { ...basePayload, subscription_id: subscriptionId })
  }, [trackEvent, basePayload])

  const trackRenewSuccess = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RENEW_SUCCESS, { ...basePayload, subscription_id: subscriptionId })
  }, [trackEvent, basePayload])

  const trackRenewFailure = useCallback((subscriptionId, errorMessage) => {
    trackEvent(SubscriptionEvents.RENEW_FAILURE, {
      ...basePayload,
      subscription_id: subscriptionId,
      error_message: errorMessage,
    })
  }, [trackEvent, basePayload])

  const trackReactivateClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.REACTIVATE_CLICK, { ...basePayload, subscription_id: subscriptionId })
  }, [trackEvent, basePayload])

  const trackReactivateSuccess = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.REACTIVATE_SUCCESS, { ...basePayload, subscription_id: subscriptionId })
  }, [trackEvent, basePayload])

  const trackReactivateFailure = useCallback((subscriptionId, errorMessage) => {
    trackEvent(SubscriptionEvents.REACTIVATE_FAILURE, {
      ...basePayload,
      subscription_id: subscriptionId,
      error_message: errorMessage,
    })
  }, [trackEvent, basePayload])

  const trackPauseOfferViewed = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_OFFER_VIEWED, { ...cancelFlowPayload, subscription_id: subscriptionId })
  }, [trackEvent, cancelFlowPayload])

  const trackPauseClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_CLICK, { ...cancelFlowPayload, subscription_id: subscriptionId })
  }, [trackEvent, cancelFlowPayload])

  const trackPauseOfferDeclined = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_OFFER_DECLINED, { ...cancelFlowPayload, subscription_id: subscriptionId })
  }, [trackEvent, cancelFlowPayload])

  const trackPauseOfferDismissed = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_OFFER_DISMISSED, { ...cancelFlowPayload, subscription_id: subscriptionId })
  }, [trackEvent, cancelFlowPayload])

  const trackPauseScheduled = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_SCHEDULED, { ...cancelFlowPayload, subscription_id: subscriptionId })
  }, [trackEvent, cancelFlowPayload])

  const trackPauseFailure = useCallback((subscriptionId, errorCode) => {
    trackEvent(SubscriptionEvents.PAUSE_FAILURE, {
      ...cancelFlowPayload,
      subscription_id: subscriptionId,
      error_message: errorCode,
    })
  }, [trackEvent, cancelFlowPayload])

  const trackPauseResumeClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.PAUSE_RESUME_CLICK, { ...basePayload, subscription_id: subscriptionId })
  }, [trackEvent, basePayload])

  // Callers that navigate on success pass onComplete so GTM can dispatch the hit first --
  // reloading in the same tick tore the document down and lost events. The local timer is not
  // redundant with eventTimeout: a blocked or missing container consumes no dataLayer push at
  // all, so neither the callback nor GTM's own timeout would ever run and navigation would be
  // dropped entirely.
  const trackPauseResumeSuccess = useCallback((subscriptionId, onComplete) => {
    const payload = { ...basePayload, subscription_id: subscriptionId }

    if (typeof onComplete !== 'function') {
      trackEvent(SubscriptionEvents.PAUSE_RESUME_SUCCESS, payload)
      return
    }

    let navigated = false
    const navigateOnce = () => {
      if (navigated) return

      navigated = true
      onComplete()
    }

    trackEvent(SubscriptionEvents.PAUSE_RESUME_SUCCESS, payload, {
      eventCallback: navigateOnce,
      eventTimeout: RESUME_SUCCESS_FLUSH_TIMEOUT_MS,
    })
    setTimeout(navigateOnce, RESUME_SUCCESS_FLUSH_TIMEOUT_MS)
  }, [trackEvent, basePayload])

  const trackPauseResumeFailure = useCallback((subscriptionId, errorCode) => {
    trackEvent(SubscriptionEvents.PAUSE_RESUME_FAILURE, {
      ...basePayload,
      subscription_id: subscriptionId,
      error_message: errorCode,
    })
  }, [trackEvent, basePayload])

  const trackRetryClick = useCallback((subscriptionId) => {
    trackEvent(SubscriptionEvents.RETRY_PAYMENT_CLICK, { ...basePayload, subscription_id: subscriptionId })
  }, [trackEvent, basePayload])

  const trackPaymentHistoryViewed = useCallback(() => {
    trackEvent(SubscriptionEvents.PAYMENT_HISTORY_VIEWED, basePayload)
  }, [trackEvent, basePayload])

  const trackInvoiceDownloadClick = useCallback((subscriptionId, paymentId) => {
    trackEvent(SubscriptionEvents.INVOICE_DOWNLOAD_CLICK, {
      ...basePayload,
      subscription_id: subscriptionId,
      payment_id: paymentId,
    })
  }, [trackEvent, basePayload])

  const trackInvoiceDownloadFailed = useCallback((subscriptionId, paymentId, errorMessage) => {
    trackEvent(SubscriptionEvents.INVOICE_DOWNLOAD_FAILED, {
      ...basePayload,
      subscription_id: subscriptionId,
      payment_id: paymentId,
      error_message: errorMessage,
    })
  }, [trackEvent, basePayload])

  return {
    trackSectionView,
    trackCancelClick,
    trackCancellationConfirmed,
    trackRenewClick,
    trackRenewSuccess,
    trackRenewFailure,
    trackReactivateClick,
    trackReactivateSuccess,
    trackReactivateFailure,
    trackRetryClick,
    trackPauseOfferViewed,
    trackPauseClick,
    trackPauseOfferDeclined,
    trackPauseOfferDismissed,
    trackPauseScheduled,
    trackPauseFailure,
    trackPauseResumeClick,
    trackPauseResumeSuccess,
    trackPauseResumeFailure,
    trackPaymentHistoryViewed,
    trackInvoiceDownloadClick,
    trackInvoiceDownloadFailed,
  }
}

export default useSubscriptionSettingsTracking
